import { useState } from "react";
import { toast } from "sonner";

import {
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  TextArea,
  TextInput,
  NumberInput,
} from "../crm/Form";
import api, { formatApiError } from "../../lib/api";

/**
 * Used for both create + edit. Pass `plan` to edit; omit for create.
 * On success, calls `onSaved(plan)`.
 */
export default function PlanForm({ open, onClose, plan, onSaved }) {
  const isEdit = !!plan;
  const [code, setCode] = useState(plan?.code || "");
  const [name, setName] = useState(plan?.name || "");
  const [description, setDescription] = useState(plan?.description || "");
  const [cadence, setCadence] = useState(
    plan?.default_billing_cadence || "monthly"
  );
  const [amount, setAmount] = useState(
    plan?.default_amount_cents ? plan.default_amount_cents / 100 : 0
  );
  const [features, setFeatures] = useState(
    (plan?.included_features || []).join("\n")
  );
  const [isActive, setIsActive] = useState(plan?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const body = {
      name,
      description,
      default_billing_cadence: cadence,
      default_amount_cents: Math.max(0, Math.round(Number(amount) * 100)),
      included_features: features
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
      is_active: isActive,
    };
    if (!isEdit) body.code = code;
    try {
      const { data } = isEdit
        ? await api.patch(`/protection-plans/${plan.id}`, body)
        : await api.post("/protection-plans/", body);
      toast.success(isEdit ? "Plan updated" : "Plan created");
      onSaved(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Plan" : "New Protection Plan"}
      subtitle={
        isEdit
          ? "Catalog edits do NOT change existing subscriber agreements"
          : "Catalog template — pricing is snapshotted at enrollment"
      }
      data-testid="plan-form-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <ErrorBanner message={err} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" required hint="lowercase, digits, underscore only">
            <TextInput
              value={code}
              onChange={setCode}
              required
              disabled={isEdit}
              pattern="[a-z0-9_]+"
              data-testid="plan-code"
              placeholder="silver_monthly"
            />
          </Field>
          <Field label="Name" required>
            <TextInput
              value={name}
              onChange={setName}
              required
              data-testid="plan-name"
              placeholder="Silver — Monthly"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Cadence" required>
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              data-testid="plan-cadence"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </Field>
          <Field label="Amount (USD)" required>
            <NumberInput
              value={amount}
              onChange={setAmount}
              min={0}
              step={0.01}
              required
              data-testid="plan-amount"
            />
          </Field>
        </div>
        <Field label="Description">
          <TextArea
            value={description}
            onChange={setDescription}
            rows={2}
            data-testid="plan-description"
          />
        </Field>
        <Field
          label="Included features"
          hint="One per line — shown to ops during enrollment"
        >
          <TextArea
            value={features}
            onChange={setFeatures}
            rows={4}
            data-testid="plan-features"
            placeholder={
              "Annual visual inspection\nPriority service dispatch\nDiscounted parts on RMAs"
            }
          />
        </Field>
        <div className="flex items-center gap-2">
          <input
            id="plan-is-active"
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            data-testid="plan-is-active"
            className="h-4 w-4 rounded border-slate-300 text-amber-600"
          />
          <label htmlFor="plan-is-active" className="text-xs text-slate-700">
            Active — show on enrollment dropdown
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={busy} data-testid="plan-submit">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create plan"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
